/** Formats a raw Brazilian phone string → "(11) 94210-0040" */
export function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

/** Returns a wa.me URL for a Brazilian phone number */
export function whatsappUrl(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  const number = digits.startsWith('55') ? digits : `55${digits}`;
  return `https://wa.me/${number}`;
}

/** Brazilian numbers: 11 digits for mobile (DD + 9 + 8), 10 for landline (DD + 8). */
const MAX_PHONE_DIGITS = 11;

/**
 * Progressive input mask for a telefone field.
 *
 * formatPhone only handles COMPLETE numbers — it returns the input untouched at any other
 * length, which is useless for a field being typed into. This formats whatever is there so far
 * and caps the length, so the input cannot accept 40 characters or letters.
 *
 * The grouping depends on the total length, which the previous inline mask got wrong: it split
 * 5+4 unconditionally, rendering a 10-digit landline as "(12) 34567-890" instead of
 * "(12) 3456-7890". Mobile numbers group 5+4, landlines 4+4.
 */
export function maskPhoneInput(raw: string): string {
  let digits = (raw || '').replace(/\D/g, '');

  // A pasted "+55 (12) 98888-8888" arrives as 13 digits. A Brazilian number is at most 11, so
  // 12-13 digits beginning with 55 can only be the country code — unlike a bare 11-digit value,
  // where a leading 55 is the Santa Maria/RS area code and must be kept.
  if (digits.length > MAX_PHONE_DIGITS && digits.startsWith('55')) {
    digits = digits.slice(2);
  }
  digits = digits.slice(0, MAX_PHONE_DIGITS);

  if (digits.length === 0) return '';
  if (digits.length <= 2) return digits;

  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);

  // A Brazilian mobile always begins with 9 after the DDD; landlines never do. So the split
  // point is known from that single digit, and the hyphen never jumps position as more digits
  // are typed — a length-based rule would render "(12) 9888-8" and then re-flow to
  // "(12) 98888-8888" at the eleventh digit.
  const isMobile = rest.startsWith('9');
  const splitAt = isMobile ? 5 : 4;

  if (rest.length <= splitAt) return `(${ddd}) ${rest}`;

  return `(${ddd}) ${rest.slice(0, splitAt)}-${rest.slice(splitAt)}`;
}
